import React, { useState, useEffect } from 'react';
import { getPollinationsUsage } from '@/services/pollinations';
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Download, Search, ChevronLeft, ChevronRight, Loader2, FileJson, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';

const PollinationsUsageHistory = () => {
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [history, setHistory] = useState([]);
  const [limit] = useState(50);

  const fetchUsage = async (before = null) => {
    setLoading(true);
    try {
      const { data, error: apiError } = await getPollinationsUsage({ limit, before });
      if (apiError) {
        setError(apiError);
      } else {
        // Handle cases where data might be an object containing the array or a direct array
        const usageData = Array.isArray(data) ? data : (data?.usage || data?.logs || []);
        setUsage(usageData);
        
        if (usageData.length > 0) {
          setCursor(usageData[usageData.length - 1].timestamp);
        }
      }
    } catch (err) {
      setError("Failed to fetch usage data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsage();
  }, []);

  const handleNext = () => {
    if (usage.length < limit) return;
    setHistory([...history, cursor]);
    fetchUsage(cursor);
  };

  const handlePrevious = () => {
    const newHistory = [...history];
    newHistory.pop();
    const prevCursor = newHistory.length > 0 ? newHistory[newHistory.length - 1] : null;
    setHistory(newHistory);
    fetchUsage(prevCursor);
  };

  const exportData = (format) => {
    if (!Array.isArray(usage) || usage.length === 0) return;
    
    if (format === 'json') {
      const blob = new Blob([JSON.stringify(usage, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pollinations-usage-${new Date().toISOString()}.json`;
      link.click();
    } else {
      const headers = Object.keys(usage[0]);
      const csv = [
        headers.join(','),
        ...usage.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
      ].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pollinations-usage-${new Date().toISOString()}.csv`;
      link.click();
    }
    toast.success(`Exported current view as ${format.toUpperCase()}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-syne font-bold">Inference Logs</h1>
          <p className="text-sonas-muted text-xs">Granular breakdown of every AI interaction.</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button onClick={() => exportData('json')} variant="outline" className="h-9 gap-2 border-sonas-border rounded-xl text-xs font-bold">
            <FileJson size={14} /> JSON
          </Button>
          <Button onClick={() => exportData('csv')} variant="outline" className="h-9 gap-2 border-sonas-border rounded-xl text-xs font-bold">
            <FileSpreadsheet size={14} /> CSV
          </Button>
        </div>
      </div>

      <div className="bg-sonas-surface border-sonas-border rounded-2xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto sonas-scrollbar">
          <Table>
            <TableHeader className="bg-sonas-surface2/50">
              <TableRow className="hover:bg-transparent border-sonas-border">
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-sonas-dim">Timestamp</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-sonas-dim">Type</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-sonas-dim">Model</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-sonas-dim">Tokens (I/O)</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-sonas-dim">Cost</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-sonas-dim text-right">Latency</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="divide-y divide-sonas-border">
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-64 text-center">
                    <Loader2 className="animate-spin h-6 w-6 mx-auto text-sonas-dim" />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-64 text-center text-red-500 font-bold">{error}</TableCell>
                </TableRow>
              ) : !Array.isArray(usage) || usage.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-64 text-center text-sonas-dim uppercase text-[10px] font-bold tracking-widest">No logs found</TableCell>
                </TableRow>
              ) : usage.map((log, idx) => (
                <TableRow key={idx} className="hover:bg-sonas-surface2/30 border-sonas-border transition-colors group">
                  <TableCell className="text-xs font-mono text-sonas-muted">
                    {log.timestamp ? new Date(log.timestamp).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                  </TableCell>
                  <TableCell className="text-xs font-bold capitalize">{log.requestType || 'N/A'}</TableCell>
                  <TableCell className="text-xs font-medium text-sonas-accent">{log.model || 'N/A'}</TableCell>
                  <TableCell className="text-xs font-mono">
                    {log.inputTextTokens || 0}/{log.outputTextTokens || 0}
                  </TableCell>
                  <TableCell className="text-xs font-bold font-mono">
                    ${log.cost?.toFixed(4) || '0.0000'}
                  </TableCell>
                  <TableCell className="text-xs font-mono text-right">
                    {log.responseTime || 0}ms
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4">
        <p className="text-[10px] font-bold uppercase tracking-widest text-sonas-dim">
          Showing batch of {Array.isArray(usage) ? usage.length : 0} nodes
        </p>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="icon" 
            disabled={history.length === 0 || loading} 
            onClick={handlePrevious}
            className="h-9 w-9 border-sonas-border rounded-xl"
          >
            <ChevronLeft size={16} />
          </Button>
          <Button 
            variant="outline" 
            size="icon" 
            disabled={!Array.isArray(usage) || usage.length < limit || loading} 
            onClick={handleNext}
            className="h-9 w-9 border-sonas-border rounded-xl"
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PollinationsUsageHistory;